import { useEffect, useState, useCallback } from "react";
import { opApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { CalendarCheck, UserCheck, XCircle, Repeat, DollarSign, UserPlus, CheckCircle2, Clock } from "lucide-react";

const ICON = { booked_online: CalendarCheck, booked_by_team: CalendarCheck, walk_in: UserPlus, checked_in: UserCheck, confirmed: CheckCircle2, completed: CheckCircle2, cancelled: XCircle, cancelled_by_client: XCircle, no_show: Clock, rescheduled_by_client: Repeat, deposit: DollarSign, booked: Repeat };
const ROLE = { owner: "Owner", manager: "Manager", staff: "Staff", client: "Client", support: "MorrCart support" };
const sel = "border border-input bg-background h-9 px-2 text-sm";
const ago = (d) => new Date(d).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// Plain-English calendar history for owners/managers: who did what, to which booking, when.
export function ActivityLog({ providers }) {
  const [f, setF] = useState({ provider_id: "any", actor: "any", date_from: "", date_to: "" });
  const [data, setData] = useState({ entries: [], actors: [] });
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "any"));
    opApi.get("/tenant/appointments/activity", { params }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div data-testid="activity-log">
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <select className={sel} value={f.provider_id} onChange={(e) => set("provider_id", e.target.value)} data-testid="activity-filter-provider">
          <option value="any">All providers</option>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className={sel} value={f.actor} onChange={(e) => set("actor", e.target.value)} data-testid="activity-filter-actor">
          <option value="any">Anyone</option>{data.actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Input type="date" className="rounded-none w-auto" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="activity-from" />
        <span className="text-sm text-muted-foreground">to</span>
        <Input type="date" className="rounded-none w-auto" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="activity-to" />
      </div>
      {data.entries.length === 0 && <div className="text-sm text-muted-foreground border border-dashed p-6 text-center" data-testid="activity-empty">No calendar activity for these filters yet.</div>}
      <ol className="divide-y border border-border" data-testid="activity-list">
        {data.entries.map((e) => {
          const Icon = ICON[e.kind] || CalendarCheck;
          return (
            <li key={e.id} className="flex gap-3 p-3 text-sm" data-testid={`activity-${e.id}`}>
              <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <div className="flex-1">
                <div>{e.text}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{ago(e.at)} · {ROLE[e.actor?.role] || e.actor?.role}{e.ref ? ` · ${e.ref}` : ""}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
